import React, { useState } from 'react';
import { Button, notification, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Content } from 'antd/lib/layout/layout';

import { useAppDispatch, useAppSelector } from 'store';
import { MainLayout } from 'components/Layout/MainLayout';
import { useQuery } from 'hooks/useQuery';
import { useDeepEffect } from 'utils/useDeepEffect';
import { StickyConfirmModal } from 'components/modals/StickyConfirmModal';
import { PageTitles } from 'constants/pageTitles';
import {
  loadTouristRoutesList,
  setPublishTouristRoutes,
  setUnPublishTouristRoutes,
  removeTouristRoutesItem,
} from 'store/slices/touristRoutes/actions';
import { StatusesEnum } from 'constants/status';
import { canBePublished, canBeUnPublished } from 'utils/rights';
import { LangEnum } from 'constants/lang';

import { Filters } from './components/Filters';
import { ListTable } from './components/ListTable';

export const List = () => {
  const [sort, setSort] = React.useState(null);
  const dispatch = useAppDispatch();
  const query = useQuery();

  const { list, total, limit } = useAppSelector(state => state.touristRoutes);
  const { b2cUrl } = useAppSelector(state => state.configSlice);
  const loadTouristRoutes = () => {
    dispatch(
      loadTouristRoutesList({
        ...query,
        sort,
      })
    );
  };

  const [modalIsOpen, setModalIsOpen] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  useDeepEffect(() => {
    loadTouristRoutes();
  }, [query, sort]);

  const modalCloseHandler = () => {
    setModalIsOpen(null);
    setCurrentItem(null);
  };

  const handleRemove = async () => {
    await dispatch(removeTouristRoutesItem(currentItem))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно удалена',
        });
        loadTouristRoutes();
        modalCloseHandler();
      });
  };

  const handlePublish = async id => {
    await dispatch(setPublishTouristRoutes(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно опубликована',
        });
        loadTouristRoutes();
        modalCloseHandler();
      });
  };

  const handleUnPublish = async id => {
    await dispatch(setUnPublishTouristRoutes(id))
      .unwrap()
      .then(() => {
        notification.success({
          message: 'Cущность успешно снята с публикации',
        });
        loadTouristRoutes();
        modalCloseHandler();
      });
  };

  const getActions = item => [
    item.status === StatusesEnum.PUBLISHED && {
      element: (
        <a
          target='_blank'
          rel='noreferrer nofollow'
          href={`${b2cUrl}/${item.lang}/touristRoutes/${item.id}/${item.sysName}`}
        >
          На сайте
        </a>
      ),
    },
    {
      element: <Link to={`/touristRoutes/${item.id}/edit`}>Редактировать</Link>,
    },
    item.lang === LangEnum.ru && {
      element: (
        <Link to={`/touristRoutes/${item.id}/add-translation`}>
          Создать копию для перевода
        </Link>
      ),
    },
    canBePublished(item) && {
      element: <Typography.Text>Опубликовать</Typography.Text>,
      handler: () => handlePublish(item.id),
    },
    canBeUnPublished(item) && {
      element: <Typography.Text>Снять с публикации</Typography.Text>,
      handler: () => handleUnPublish(item.id),
    },
    {
      element: <Typography.Text type='danger'>Удалить</Typography.Text>,
      handler: () => {
        if (item.status === StatusesEnum.PUBLISHED) {
          notification.error({
            message: 'Необходимо снять сущность с публикации',
          });
        } else {
          setModalIsOpen(true);
          setCurrentItem(item);
        }
      },
    },
  ];

  return (
    <MainLayout
      pageTitle={PageTitles.touristRoutes}
      extraButtonsList={[
        <Link key={1} to='/touristRoutes/add'>
          <Button type='primary'>Создать</Button>
        </Link>,
      ]}
      aside={<Filters />}
    >
      <Content>
        <ListTable
          data={list}
          total={total}
          getActions={getActions}
          limit={limit}
          setSort={setSort}
        />
      </Content>
      {currentItem?.id && (
        <>
          {modalIsOpen && (
            <StickyConfirmModal
              isOpen={modalIsOpen}
              onRequestClose={modalCloseHandler}
              title='Удаление сущности'
              okText='Удалить'
              text='Вы действительно хотите удалить эту cущность?'
              onSubmit={handleRemove}
            />
          )}
        </>
      )}
    </MainLayout>
  );
};
